import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  TextField, Switch, FormControlLabel, Snackbar, Alert,
  CircularProgress, Tooltip, Grid, TableSortLabel
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Tour as TourIcon } from '@mui/icons-material';
import axios from 'axios';
import { extractArrayFromResponse } from '../utils/apiHelpers';
import { ColumnToggle } from './common/ColumnToggle';
import { Modal } from './common/Modal';

interface Tour {
  id: number;
  name: string;
  price_summer: number;
  price_winter: number;
  price_eng: number;
  is_group: boolean;
  is_active: boolean | number;
  duration: string;
}

type Order = 'asc' | 'desc';
type SortableKey = 'id' | 'name' | 'price_summer' | 'price_winter' | 'price_eng' | 'is_group' | 'is_active' | 'duration';

const toBoolean = (value: boolean | number | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  return false;
};

const ToursPage: React.FC = () => {
  // ВСЕ ХУКИ СТРОГО В НАЧАЛЕ КОМПОНЕНТА
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTour, setEditingTour] = useState<Tour | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    name: '',
    price_summer: 0,
    price_winter: 0,
    price_eng: 0,
    is_group: false,
    is_active: true,
    duration: ''
  });

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKey>('id');

  const columns = [
    { key: 'id', label: 'ID', defaultVisible: true },
    { key: 'name', label: 'Название', defaultVisible: true },
    { key: 'duration', label: 'Длит.', defaultVisible: true }, // Добавлена колонка
    { key: 'price_summer', label: 'Лето', defaultVisible: true },
    { key: 'price_winter', label: 'Зима', defaultVisible: true },
    { key: 'price_eng', label: 'Англ', defaultVisible: true },
    { key: 'is_group', label: 'Тип', defaultVisible: true },
    { key: 'is_active', label: 'Статус', defaultVisible: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  // ИСПРАВЛЕННЫЙ USEEFFECT: гарантирует наличие duration в видимых колонках
  useEffect(() => {
    const saved = localStorage.getItem('tours_visible_columns');
    let cols: string[] = [];
    if (saved) {
      try {
        cols = JSON.parse(saved);
      } catch (e) {}
    }
    if (cols.length === 0) {
      cols = columns.filter(c => c.defaultVisible).map(c => c.key);
    }
    // Принудительно добавляем duration, если его нет в сохраненных настройках
    if (!cols.includes('duration')) {
      cols.push('duration');
    }
    setVisibleColumns(cols);
  }, []);

  const loadTours = async () => {
    try {
      setLoading(true);
      const response = await axios.get('http://192.168.50.43:5002/api/tours');
      const data = extractArrayFromResponse<Tour>(response.data);
      setTours(data);
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки данных', severity: 'error' });
      setTours([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTours();
  }, []);

  const handleToggleActive = async (tour: Tour) => {
    const currentStatus = toBoolean(tour.is_active);
    const newStatus = !currentStatus;

    try {
      await axios.put(`http://192.168.50.43:5002/api/tours/${tour.id}`, {
        name: tour.name,
        price_summer: tour.price_summer,
        price_winter: tour.price_winter,
        price_eng: tour.price_eng,
        is_group: tour.is_group,
        is_active: newStatus,
        duration: tour.duration // Передаем duration при обновлении статуса
      });

      setTours(prev => prev.map(t =>
        t.id === tour.id ? { ...t, is_active: newStatus } : t
      ));

      setSnackbar({
        open: true,
        message: `Статус изменён на ${newStatus ? 'Активна' : 'Неактивна'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Ошибка обновления статуса:', error);
      setSnackbar({ open: true, message: 'Ошибка изменения статуса', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      setSnackbar({ open: true, message: 'Введите название', severity: 'error' });
      return;
    }
    try {
      if (editingTour) {
        await axios.put(`http://192.168.50.43:5002/api/tours/${editingTour.id}`, formData);
        setSnackbar({ open: true, message: 'Программа обновлена', severity: 'success' });
      } else {
        await axios.post('http://192.168.50.43:5002/api/tours', formData);
        setSnackbar({ open: true, message: 'Программа создана', severity: 'success' });
      }
      setOpenDialog(false);
      loadTours();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить программу?')) return;
    try {
      await axios.delete(`http://192.168.50.43:5002/api/tours/${id}`);
      setSnackbar({ open: true, message: 'Программа удалена', severity: 'success' });
      loadTours();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
    }
  };

  const handleRequestSort = (property: SortableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedTours = [...tours].sort((a, b) => {
    let aValue: any = a[orderBy];
    let bValue: any = b[orderBy];

    if (orderBy === 'is_active') {
      aValue = toBoolean(a.is_active) ? 1 : 0;
      bValue = toBoolean(b.is_active) ? 1 : 0;
    }

    if (orderBy === 'is_group') {
      aValue = a.is_group ? 1 : 0;
      bValue = b.is_group ? 1 : 0;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isVisible = (key: string) => visibleColumns.includes(key);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadTours} size="small">Обновить</Button>
          {/* ИСПРАВЛЕНА КНОПКА ДОБАВЛЕНИЯ: добавлен duration */}
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => { 
              setEditingTour(null); 
              setFormData({ 
                name: '', 
                price_summer: 0, 
                price_winter: 0, 
                price_eng: 0, 
                is_group: false, 
                is_active: true,
                duration: '' 
              }); 
              setOpenDialog(true); 
            }}
          >
            Добавить
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ColumnToggle
            columns={columns}
            storageKey="tours_visible_columns"
            onChange={(cols) => setVisibleColumns(cols)}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                {isVisible('id') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleRequestSort('id')} sx={{ fontWeight: 'bold' }}>ID</TableSortLabel>
                  </TableCell>
                )}
                {isVisible('name') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleRequestSort('name')} sx={{ fontWeight: 'bold' }}>Название</TableSortLabel>
                  </TableCell>
                )}
                {/* ДОБАВЛЕН ЗАГОЛОВОК КОЛОНКИ ДЛИТЕЛЬНОСТЬ */}
                {isVisible('duration') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'duration'} direction={orderBy === 'duration' ? order : 'asc'} onClick={() => handleRequestSort('duration')} sx={{ fontWeight: 'bold' }}>Длит.</TableSortLabel>
                  </TableCell>
                )}
                {isVisible('price_summer') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'price_summer'} direction={orderBy === 'price_summer' ? order : 'asc'} onClick={() => handleRequestSort('price_summer')} sx={{ fontWeight: 'bold' }}>Лето</TableSortLabel>
                  </TableCell>
                )}
                {isVisible('price_winter') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'price_winter'} direction={orderBy === 'price_winter' ? order : 'asc'} onClick={() => handleRequestSort('price_winter')} sx={{ fontWeight: 'bold' }}>Зима</TableSortLabel>
                  </TableCell>
                )}
                {isVisible('price_eng') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'price_eng'} direction={orderBy === 'price_eng' ? order : 'asc'} onClick={() => handleRequestSort('price_eng')} sx={{ fontWeight: 'bold' }}>Англ</TableSortLabel>
                  </TableCell>
                )}
                {isVisible('is_group') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'is_group'} direction={orderBy === 'is_group' ? order : 'asc'} onClick={() => handleRequestSort('is_group')} sx={{ fontWeight: 'bold' }}>Тип</TableSortLabel>
                  </TableCell>
                )}
                {isVisible('is_active') && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'is_active'} direction={orderBy === 'is_active' ? order : 'asc'} onClick={() => handleRequestSort('is_active')} sx={{ fontWeight: 'bold' }}>Статус</TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>Нет программ</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedTours.map((tour) => {
                  const isActive = toBoolean(tour.is_active);
                  return (
                    <TableRow key={tour.id} hover sx={{ opacity: isActive ? 1 : 0.6, bgcolor: isActive ? 'inherit' : '#f5f5f5', '&:hover': { bgcolor: isActive ? 'action.hover' : '#e8e8e8' } }}>
                      {isVisible('id') && <TableCell>{tour.id}</TableCell>}
                      {isVisible('name') && <TableCell sx={{ maxWidth: '200px', wordWrap: 'break-word' }}>{tour.name}</TableCell>}
                      {/* ДОБАВЛЕНА ЯЧЕЙКА ДЛИТЕЛЬНОСТИ */}
                      {isVisible('duration') && <TableCell>{tour.duration || '—'}</TableCell>}
                      {isVisible('price_summer') && <TableCell>{tour.price_summer} ₽</TableCell>}
                      {isVisible('price_winter') && <TableCell>{tour.price_winter} ₽</TableCell>}
                      {isVisible('price_eng') && <TableCell>{tour.price_eng} ₽</TableCell>}
                      {isVisible('is_group') && (
                        <TableCell>
                          <Chip label={tour.is_group ? 'Групповая' : 'Индивидуальная'} size="small" color={tour.is_group ? 'primary' : 'default'} />
                        </TableCell>
                      )}
                      {isVisible('is_active') && (
                        <TableCell>
                          <Switch checked={isActive} onChange={() => handleToggleActive(tour)} color="success" size="small" />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Tooltip title="Редактировать">
                          {/* ИСПРАВЛЕН ОБРАБОТЧИК РЕДАКТИРОВАНИЯ: добавлен duration */}
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => { 
                              setEditingTour(tour); 
                              setFormData({ 
                                name: tour.name, 
                                price_summer: tour.price_summer, 
                                price_winter: tour.price_winter, 
                                price_eng: tour.price_eng, 
                                is_group: tour.is_group, 
                                is_active: isActive,
                                duration: tour.duration || '' 
                              }); 
                              setOpenDialog(true); 
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => handleDelete(tour.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingTour ? 'Редактирование программы' : 'Новая программа'}
        actions={
          <>
            <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 500 }}>Отмена</Button>
            <Button onClick={handleSave} variant="contained" sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 500 }}>{editingTour ? 'Обновить' : 'Создать'}</Button>
          </>
        }
      >
        <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Название"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
          />
          
          {/* ДОБАВЛЕНО ПОЛЕ ДЛИТЕЛЬНОСТЬ В ФОРМУ */}
          <TextField
            label="Длительность"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            fullWidth
            variant="outlined"
            placeholder="например: 1.5 часа или 90 мин"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Лето" type="number" value={formData.price_summer} onChange={(e) => setFormData({ ...formData, price_summer: parseFloat(e.target.value) || 0 })} fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Зима" type="number" value={formData.price_winter} onChange={(e) => setFormData({ ...formData, price_winter: parseFloat(e.target.value) || 0 })} fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Англ" type="number" value={formData.price_eng} onChange={(e) => setFormData({ ...formData, price_eng: parseFloat(e.target.value) || 0 })} fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }} />
            </Grid>
          </Grid>
          <FormControlLabel control={<Switch checked={formData.is_group} onChange={(e) => setFormData({ ...formData, is_group: e.target.checked })} />} label={formData.is_group ? 'Групповая' : 'Индивидуальная'} sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: formData.is_group ? '#1976d2' : '#757575' } }} />
          <FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} color="success" />} label={formData.is_active ? 'Активна' : 'Неактивна'} sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: formData.is_active ? '#2e7d32' : '#d32f2f' } }} />
        </Box>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ToursPage;